'use client';
import { useMemo, useState } from 'react';

import { useUrlSearchSync } from '@/hooks/shared/useUrlSearchSync';
import {
  useBiggestWinners,
  useLeaderboardCategories,
  useLeaderboardData,
  useLeaderboardTimePeriods,
} from '@/services/query';
import { LeaderboardQueryParams, OrderBy, TimeFilter } from '@/types';

const formatCategoryForApi = (category: string): string => {
  if (category === 'All Categories') return 'OVERALL';
  return category.toUpperCase();
};

export const useLeaderboard = () => {
  const [selectedTimeFilter, setSelectedTimeFilter] = useState<TimeFilter>('DAY');
  const [selectedCategory, setSelectedCategory] = useState('OVERALL');
  const [orderBy, setOrderBy] = useState<OrderBy>('PNL');

  // --- 1. Shared Infrastructure ---
  const { searchQuery, setSearchQuery, debouncedSearchQuery } = useUrlSearchSync(300);

  // --- 2. Data Fetching ---
  const { data: rawTimePeriods = [] } = useLeaderboardTimePeriods();
  const { data: rawCategories = [] } = useLeaderboardCategories();

  const queryParams: LeaderboardQueryParams = {
    timePeriod: selectedTimeFilter,
    category: formatCategoryForApi(selectedCategory),
    limit: 20,
    userName: debouncedSearchQuery || undefined,
    orderBy: orderBy,
  };

  const {
    data: infiniteTraders,
    isLoading: isLeaderboardLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useLeaderboardData(queryParams);

  const traderData = useMemo(() => {
    return infiniteTraders?.pages.flatMap((page) => page) || [];
  }, [infiniteTraders]);

  const { data: winnersData = [], isLoading: isWinnersLoading } = useBiggestWinners({
    ...queryParams,
  });

  // --- 3. Handlers ---
  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return {
    state: {
      selectedTimeFilter,
      searchQuery,
      selectedCategory,
      filteredLeaderboard: traderData,
      biggestWinsData: winnersData,
      isLoading: isLeaderboardLoading || isWinnersLoading,
      categories: rawCategories,
      timePeriods: rawTimePeriods,
      orderBy,
      hasNextPage,
      isFetchingNextPage,
    },
    actions: {
      setSelectedTimeFilter,
      setSearchQuery,
      setSelectedCategory,
      setOrderBy,
      loadMore,
    },
  };
};
